import React, { useState, useEffect } from "react";
import { Table, Spin, Button, message } from "antd";
import { useLocation } from "react-router-dom";
import useCustomNavigate from "../../../components/customNavigationHook";
import API from "../../../api";
import urls from "../../../api/urls";
import moment from "moment";
import Heading from "../../../components/Heading";
import constants from "../../../constants/constants";

const { DRS_REPORT } = urls;
const { ROLES } = constants;
const { CP_ADMIN } = ROLES;

const ReportDetails = () => {
  const location = useLocation();
  const navigate = useCustomNavigate();
  const userinfo = JSON.parse(localStorage.getItem("userinfo"));
  const [awbDetails, setAwbDetails] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalCount: 0,
    limit: 10,
  });

  // Parse URL parameters
  const searchParams = new URLSearchParams(location.search);
  const startDate = searchParams.get("startDate");
  const endDate = searchParams.get("endDate");
  const key = searchParams.get("key");
  const currentPage = parseInt(searchParams.get("page")) || 1;
  const cpId = userinfo?.spId ? userinfo?.spId : "";
  const hubId = userinfo?.premiseId ? userinfo?.premiseId : "";

  useEffect(() => {
    if (startDate && endDate && cpId && key) {
      fetchAwbDetails(currentPage);
    }
  }, [startDate, endDate, cpId, key, currentPage]);

  const fetchAwbDetails = async (page = 1) => {
    setLoading(true);
    try {
      const isCpAdmin =
        userinfo && userinfo.roles && userinfo.roles.includes(CP_ADMIN);
      const queryParams = new URLSearchParams();
      queryParams.append("startDate", startDate);
      queryParams.append("endDate", endDate);
      queryParams.append("cpId", cpId);
      queryParams.append("key", key);
      queryParams.append("page", page.toString());
      queryParams.append("limit", "10");

      if (!isCpAdmin) {
        queryParams.append("hubId", hubId);
      }

      const result = await API.Api(
        `${DRS_REPORT}?${queryParams.toString()}`
      ).getwithParams({}, { kid: "1", authtype: "jwt" });

      const response = result?.response?.data;
      if (response && response.data) {
        setAwbDetails(response.data);
        setPagination(
          response.pagination || {
            currentPage: page,
            totalPages: 1,
            totalCount: response.data.length,
            limit: 10,
          }
        );
      } else {
        setAwbDetails([]);
        setPagination({
          currentPage: 1,
          totalPages: 1,
          totalCount: 0,
          limit: 10,
        });
      }
    } catch (error) {
      console.error("Error fetching AWB details:", error);
      message.error("Failed to fetch AWB details");
      setAwbDetails([]);
    }
    setLoading(false);
  };

  const handlePageChange = (page) => {
    // Update URL with new page parameter
    const newSearchParams = new URLSearchParams(location.search);
    newSearchParams.set("page", page.toString());
    navigate(`${location.pathname}?${newSearchParams.toString()}`, {
      replace: true,
    });
  };

  const getKeyDisplayName = (key) => {
    switch (key) {
    case "outscanned":
      return "Outscan By Hub";
    case "inscanned":
      return "Inscan By CP";
    case "notInScan":
      return "Not Inscan By CP";
    case "drsGenerationByCPOfSameDay":
      return "DRS Generation";
    case "delivered":
      return "Same Day Deliveries";
    case "undelivered":
      return "Undelivered";
    case "rto":
      return "RTO";
    default:
      return key
        ?.replace(/([A-Z])/g, " $1")
        .replace(/^./, (str) => str.toUpperCase());
    }
  };

  const columns = [
    {
      title: "AWB Number",
      dataIndex: "awbNumber",
      key: "awbNumber",
      width: 200,
      render: (text) => (
        <span
          style={{
            fontFamily: "monospace",
            fontSize: "14px",
            fontWeight: "500",
          }}
        >
          {text}
        </span>
      ),
    },
    {
      title: "Current Status",
      dataIndex: "currentStatus",
      key: "currentStatus",
      render: (text) => (
        <span
          style={{
            textTransform: "capitalize",
            color: "#666",
            fontSize: "14px",
          }}
        >
          {text}
        </span>
      ),
    },
  ];

  if (!startDate || !endDate || !cpId || !key) {
    return (
      <div className="master-main-container m-1 p-1">
        <div style={{ textAlign: "center", padding: "50px" }}>
          <h3>Invalid Parameters</h3>
          <p>
            Required parameters are missing. Please navigate from the DRS
            Report.
          </p>
          <Button
            type="primary"
            onClick={() => navigate("/delivery-mis-report")}
          >
            Go to DRS Report
          </Button>
        </div>
      </div>
    );
  }

  return (
    <Spin spinning={loading}>
      <div className="master-main-container m-1 p-1">
        <div className="mb-1">
          <Heading title="Back" navigateTo="/delivery-mis-report" />
        </div>

        <div className="page-header" style={{ marginBottom: "24px" }}>
          <h2>{getKeyDisplayName(key)} Details</h2>
          <div
            style={{
              background: "#f5f5f5",
              padding: "12px 16px",
              borderRadius: "6px",
              marginTop: "12px",
            }}
          >
            <strong> Date:</strong> {moment(startDate).format("DD-MM-YYYY")} |
            <strong> Total Records:</strong> {pagination.totalCount}
          </div>
        </div>

        <Table
          columns={columns}
          dataSource={awbDetails}
          loading={loading}
          pagination={{
            current: pagination.currentPage,
            total: pagination.totalCount,
            pageSize: pagination.limit,
            showSizeChanger: false,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} of ${total} AWBs`,
            onChange: handlePageChange,
          }}
          rowKey="awbNumber"
          size="middle"
        />
      </div>
    </Spin>
  );
};

export default ReportDetails;
